import { BlockRenderer, resolveLocalized } from '@fixit/bozp-renderer';
import { useQuery } from '@tanstack/react-query';
import { createRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getBozpToken } from '@/api/bozp-token';
import { getServerProgress, getTraining } from '@/api/training';
import { ChapterSidebar } from '@/components/ChapterSidebar';
import { DwellCountdown } from '@/components/DwellCountdown';
import { useT } from '@/i18n';
import { useLocale } from '@/i18n/locale';
import { type ChapterGateState, isChapterComplete } from '@/lib/chapter-gate';
import { deriveChapterStates, isQuizUnlocked } from '@/lib/chapter-states';
import { reportClientError, reportSessionOpenFailed } from '@/lib/client-report';
import { decodeJwtSub } from '@/lib/jwt';
import {
  clearProgress,
  loadProgress,
  saveProgress,
  type TrainingProgress,
} from '@/lib/progress-db';
import { createTelemetryQueue, type TelemetryQueue } from '@/lib/telemetry';
import { clearTrainingSession, ensureTrainingSession } from '@/lib/training-session';
import { Route as rootRoute } from './__root';

/**
 * Put a freshly opened chapter at the top - from whichever element actually scrolls.
 *
 * `<main>` carries `overflow-y-auto`, but the root shell is `min-h-dvh`: nothing caps the
 * height, so main stretches to its content and the DOCUMENT is the real scroller. Scrolling
 * only the element was therefore a silent no-op, and the worker landed mid-chapter on every
 * Continue. Reset both, so this stays correct if the shell is ever given a fixed height and
 * main becomes the scroller for real.
 */
function resetChapterScroll(content: HTMLElement | null) {
  content?.scrollTo({ top: 0 });
  // The player runs inside an iframe (web embed) and standalone; both scroll their own window.
  content?.ownerDocument.defaultView?.scrollTo({ top: 0 });
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/training/$hazardId',
  component: TrainingPage,
});

function TrainingPage() {
  const t = useT();
  const LOCALE = useLocale();
  const navigate = useNavigate();
  const { hazardId } = useParams({ from: '/training/$hazardId' });
  const userId = useMemo(() => decodeJwtSub(getBozpToken() ?? '') ?? 'anon', []);

  const {
    data: training,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['training', hazardId],
    queryFn: () => getTraining(hazardId),
  });

  // Server-owned session (decision B2): opened for the published version the
  // content came from; every event and the final submit reference it.
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState(false);
  const [sessionAttempt, setSessionAttempt] = useState(0);
  const [recovery, setRecovery] = useState<'restart' | 'reauthenticate' | null>(null);
  const [restarting, setRestarting] = useState(false);
  const [restartError, setRestartError] = useState(false);
  const telemetry = useRef<TelemetryQueue | null>(null);

  const [completedIds, setCompletedIds] = useState<string[]>([]);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [gate, setGate] = useState<
    ChapterGateState & { chapterId: string | null; sessionId: string | null }
  >({
    chapterId: null,
    sessionId: null,
    scrolledToEnd: false,
    widgetIds: [],
    dwellSeconds: 0,
  });
  const [chapterAttempt, setChapterAttempt] = useState(0);
  const [deliveryError, setDeliveryError] = useState(false);
  const [openingAcknowledged, setOpeningAcknowledged] = useState(false);
  const attemptRef = useRef(0);
  const sectionPending = useRef(false);
  const contentRef = useRef<HTMLElement>(null);
  const startedRef = useRef(false);

  // Initialise once training loads: session + telemetry + resume from IndexedDB/server.
  // biome-ignore lint/correctness/useExhaustiveDependencies: explicit restart opens a fresh server session.
  useEffect(() => {
    if (!training) return;
    let cancelled = false;
    (async () => {
      let session: Awaited<ReturnType<typeof ensureTrainingSession>>;
      try {
        session = await ensureTrainingSession(training);
      } catch (error) {
        // fixit/4329: the server never sees a session that failed on the
        // device (a network drop), so the player reports it before it asks
        // the worker to try again.
        reportSessionOpenFailed(training.id, error);
        if (!cancelled) setSessionError(true);
        return;
      }
      if (cancelled) return;
      setSessionId(session.sessionId);
      telemetry.current = createTelemetryQueue({
        hazardProfileId: training.id,
        sessionId: session.sessionId,
        contentVersion: training.versionHash,
        schemaVersion: training.content.schemaVersion,
      });
      if (!startedRef.current) {
        startedRef.current = true;
        void telemetry.current.emit('started');
      }
      // Chapter events belong to the session: a resumed local checklist from
      // an older session does not exist server-side, so the resume point comes
      // from local/server progress but every chapter is re-walked in THIS
      // session (the server gate requires it).
      // A local store that cannot be read (some WebViews refuse IndexedDB)
      // must not stall the page on the landing of the chapter list: report it
      // and resume from the server's progress instead.
      const local = await loadProgress(userId, training.id).catch((error: unknown) => {
        reportClientError(error, { hazardProfileId: training.id, stage: 'resume_progress' });
        return null;
      });
      const server = await getServerProgress(training.id).catch(() => null);
      const completed =
        local?.sessionId === session.sessionId ? (local?.completedChapterIds ?? []) : [];
      const seen = local?.completedChapterIds ?? server?.completedChapterIds ?? [];
      if (cancelled) return;
      setCompletedIds(completed);
      const firstUncompleted =
        training.content.chapters.find((c) => !completed.includes(c.id))?.id ??
        training.content.chapters.find((c) => !seen.includes(c.id))?.id ??
        training.content.chapters[0]?.id ??
        null;
      setFocusedId(firstUncompleted);
    })();
    return () => {
      cancelled = true;
    };
  }, [training, userId, sessionAttempt]);

  const handleDeliveryFailure = useCallback(() => {
    const failure = telemetry.current?.failure();
    if (failure === 'restart' || failure === 'reauthenticate') setRecovery(failure);
    setDeliveryError(true);
  }, []);

  const restartTraining = async () => {
    if (!training || restarting) return;
    setRestarting(true);
    setRestartError(false);
    ++attemptRef.current;
    try {
      await clearProgress(userId, training.id);
      clearTrainingSession(training.id);
      telemetry.current = null;
      startedRef.current = false;
      setCompletedIds([]);
      setFocusedId(null);
      setSessionId(null);
      setSessionError(false);
      setRecovery(null);
      setSessionAttempt((value) => value + 1);
    } catch {
      console.error('BOZP training restart failed');
      setRestartError(true);
    } finally {
      setRestarting(false);
    }
  };

  const focusedChapter = useMemo(
    () => training?.content.chapters.find((c) => c.id === focusedId) ?? null,
    [training, focusedId],
  );

  // Reset the live gate whenever the focused chapter changes + scroll the pane
  // to top, tell the server the chapter was OPENED (its timestamp anchors the
  // dwell rule), and tick the client-side dwell clock while it stays focused.
  // biome-ignore lint/correctness/useExhaustiveDependencies: explicit retry restarts the acknowledged dwell clock.
  useEffect(() => {
    const attempt = ++attemptRef.current;
    sectionPending.current = false;
    setOpeningAcknowledged(false);
    setDeliveryError(false);
    setGate({
      chapterId: focusedId,
      sessionId,
      scrolledToEnd: false,
      widgetIds: [],
      dwellSeconds: 0,
    });
    resetChapterScroll(contentRef.current);
    if (!focusedId || !sessionId) return;
    let cancelled = false;
    let timer: number | undefined;
    void (async () => {
      const delivered = await telemetry.current?.emit('chapter_viewed', { chapterId: focusedId });
      if (cancelled || attemptRef.current !== attempt) return;
      if (!delivered) {
        handleDeliveryFailure();
        return;
      }
      setOpeningAcknowledged(true);
      const openedAt = Date.now();
      timer = window.setInterval(() => {
        setGate((g) =>
          g.chapterId === focusedId && g.sessionId === sessionId
            ? { ...g, dwellSeconds: Math.floor((Date.now() - openedAt) / 1000) }
            : g,
        );
      }, 1000);
    })();
    return () => {
      cancelled = true;
      ++attemptRef.current;
      window.clearInterval(timer);
    };
  }, [focusedId, sessionId, chapterAttempt, handleDeliveryFailure]);

  // If a chapter fits the viewport there is no scroll event to satisfy the scroll-to-end
  // gate - the worker would be stuck (Continue never enables). Treat "nothing to scroll"
  // as scrolled-to-end, re-checked when content height or the viewport changes (fonts/
  // images settling, resize, the drawer opening).
  // biome-ignore lint/correctness/useExhaustiveDependencies: retry resets scroll evidence even for the same chapter.
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !focusedId) return;
    const check = () => {
      if (el.scrollHeight <= el.clientHeight + 8) {
        setGate((g) =>
          g.chapterId !== focusedId || g.scrolledToEnd ? g : { ...g, scrolledToEnd: true },
        );
      }
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [focusedId, chapterAttempt]);

  const onScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (atEnd) setGate((g) => (g.scrolledToEnd ? g : { ...g, scrolledToEnd: true }));
  }, []);

  const onWidgetInteract = useCallback(
    (widgetId: string) => {
      setGate((g) => {
        if (g.chapterId !== focusedId) return g;
        if (g.widgetIds.includes(widgetId)) return g;
        void telemetry.current?.emit('widget_interacted', { chapterId: focusedId, widgetId });
        return { ...g, widgetIds: [...g.widgetIds, widgetId] };
      });
    },
    [focusedId],
  );

  // When the active chapter satisfies the gate, mark it complete + persist + emit section_viewed.
  useEffect(() => {
    if (!training || !focusedChapter || !openingAcknowledged || deliveryError) return;
    // Effects from this render still see the previous chapter's gate while the
    // reset above is queued. Only this chapter/session's evidence may complete it.
    if (gate.chapterId !== focusedChapter.id || gate.sessionId !== sessionId) return;
    if (completedIds.includes(focusedChapter.id)) return;
    if (!isChapterComplete(focusedChapter, gate)) return;
    if (sectionPending.current) return;
    sectionPending.current = true;
    const attempt = attemptRef.current;
    const nextCompleted = [...completedIds, focusedChapter.id];
    const progress: TrainingProgress = {
      sessionId: sessionId ?? undefined,
      chapterIdx: training.content.chapters.findIndex((c) => c.id === focusedChapter.id),
      scrollPct: 1,
      dwellByChapter: { [focusedChapter.id]: gate.dwellSeconds },
      widgetsByChapter: { [focusedChapter.id]: gate.widgetIds },
      completedChapterIds: nextCompleted,
      quizDraft: {},
      updatedAt: Date.now(),
    };
    void (async () => {
      const delivered = await telemetry.current?.emit('section_viewed', {
        chapterId: focusedChapter.id,
      });
      if (attemptRef.current !== attempt) return;
      if (!delivered) {
        handleDeliveryFailure();
        return;
      }
      // The server acknowledgment IS the evidence; IndexedDB is only the resume
      // cache. A failed write (quota, private mode) must never re-lock the chapter.
      try {
        await saveProgress(userId, training.id, progress);
      } catch (error) {
        console.error('Failed to cache acknowledged training progress', error);
      }
      if (attemptRef.current === attempt) setCompletedIds(nextCompleted);
    })();
  }, [
    gate,
    focusedChapter,
    completedIds,
    training,
    userId,
    sessionId,
    openingAcknowledged,
    deliveryError,
    handleDeliveryFailure,
  ]);

  if (recovery) {
    return (
      <div
        role="alert"
        className="mx-auto flex max-w-md flex-1 flex-col items-center justify-center gap-4 p-10 text-center"
      >
        <p>
          {recovery === 'reauthenticate'
            ? t('Return to FixIt to continue')
            : t(
                'Your training session could not be accepted. Restart the training and complete each chapter again.',
              )}
        </p>
        {recovery === 'restart' && (
          <>
            {restartError && <p>{t('Training could not be restarted. Try again.')}</p>}
            <button
              type="button"
              className="hb-btn hb-btn--primary"
              disabled={restarting}
              onClick={() => void restartTraining()}
            >
              {t('Restart training')}
            </button>
          </>
        )}
      </div>
    );
  }

  if (isLoading || (training && !sessionId && !sessionError))
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-10 text-ink-soft">
        <span aria-hidden className="hb-spinner" />
        <p>{t('Preparing your training…')}</p>
      </div>
    );
  if (training && sessionError) {
    // A session that did not open (offline, a server hiccup) is retryable:
    // the page asks again instead of leaving the worker on a dead end.
    return (
      <div
        role="alert"
        data-testid="training-session-error"
        className="mx-auto flex max-w-md flex-1 flex-col items-center justify-center gap-4 p-10 text-center"
      >
        <p>{t('Training could not be opened. Check your connection and try again.')}</p>
        <button
          type="button"
          className="hb-btn hb-btn--primary"
          data-testid="training-session-retry"
          onClick={() => {
            setSessionError(false);
            setSessionAttempt((value) => value + 1);
          }}
        >
          {t('Try again')}
        </button>
      </div>
    );
  }
  if (isError || !training) {
    return (
      <div className="mx-auto flex max-w-md flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
        <h1 className="hb-title text-2xl">{t('Training not available')}</h1>
      </div>
    );
  }

  const orderedIds = training.content.chapters.map((c) => c.id);
  const states = focusedId ? deriveChapterStates(orderedIds, completedIds, focusedId) : {};
  const quizUnlocked = isQuizUnlocked(orderedIds, completedIds);

  const idx = focusedId ? orderedIds.indexOf(focusedId) : -1;
  const isLast = idx >= 0 && idx === orderedIds.length - 1;
  const chapterDone = !!focusedId && completedIds.includes(focusedId);

  // Tailor the footer hint to whichever gate requirement is still outstanding. The dwell
  // wait is the one bounded requirement, so it gets a countdown instead of a hint.
  const dwellTotal = focusedChapter?.minDwellSeconds ?? 0;
  const dwellRemaining = Math.max(0, dwellTotal - gate.dwellSeconds);
  const dwellPending = gate.scrolledToEnd && dwellRemaining > 0;
  const gateHint = !gate.scrolledToEnd
    ? t('Keep reading to continue')
    : t('Complete the activities in this chapter');

  const goQuiz = () => navigate({ to: '/training/$hazardId/quiz', params: { hazardId } });
  // Re-reading an earlier chapter is always allowed: the server gate keeps the EARLIEST
  // chapter_viewed and the LATEST section_viewed, so a revisit can only lengthen observed
  // dwell - never invalidate a chapter the worker already cleared.
  const goBack = () => {
    const prev = orderedIds[idx - 1];
    if (!prev) return;
    setFocusedId(prev);
    setDrawerOpen(false);
  };
  const advance = () => {
    if (!chapterDone) return;
    const next = orderedIds[idx + 1];
    if (next) {
      setFocusedId(next);
      setDrawerOpen(false);
    } else if (quizUnlocked) {
      goQuiz();
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
      {/* mobile top bar - pr-20 keeps the truncating chapter title clear of the
          locale pill, which is fixed to the top right on every route */}
      <div className="flex items-center gap-3 border-b border-line bg-paper-2 py-2.5 pr-20 pl-4 lg:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="hb-btn hb-btn--ghost !min-h-0 !px-3 !py-1.5 !text-sm"
        >
          <span aria-hidden>☰</span>
          {t('Contents')}
        </button>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-soft">
          {focusedChapter ? resolveLocalized(focusedChapter.title, LOCALE) : ''}
        </span>
      </div>

      {/* drawer scrim (mobile) */}
      {drawerOpen && (
        <button
          type="button"
          aria-label={t('Close')}
          onClick={() => setDrawerOpen(false)}
          className="hb-fade fixed inset-0 z-40 bg-charcoal/40 lg:hidden"
        />
      )}

      <ChapterSidebar
        chapters={training.content.chapters}
        stateById={states}
        activeChapterId={focusedId ?? ''}
        quizUnlocked={quizUnlocked}
        locale={LOCALE}
        drawerOpen={drawerOpen}
        onSelectChapter={(id) => {
          setFocusedId(id);
          setDrawerOpen(false);
        }}
        onSelectQuiz={() => {
          setDrawerOpen(false);
          goQuiz();
        }}
        onClose={() => setDrawerOpen(false)}
      />

      {/* min-h-0 so this column can shrink instead of inheriting flexbox's default
          `min-height: auto`. Inert while the root shell is `min-h-dvh` (nothing caps the
          height, so <main> never becomes the scroller - see resetChapterScroll), but it
          is the prerequisite for making it one. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <main
          ref={contentRef}
          onScroll={onScroll}
          data-testid="chapter-content"
          className="flex-1 overflow-y-auto"
        >
          <div className="mx-auto w-full max-w-2xl px-6 py-10 sm:px-8 sm:py-14">
            {training.requiredQualificationRefs.length > 0 && (
              <aside
                data-testid="theory-only-banner"
                className="mb-8 rounded-lg border border-line bg-paper-2 px-4 py-3 text-sm text-ink-soft"
              >
                <p className="font-medium text-ink">{t('Theory course')}</p>
                <p className="mt-1">
                  {t(
                    'This course covers the theory. The professional qualification for this work is proven by a separate external exam.',
                  )}
                </p>
              </aside>
            )}
            {focusedChapter && (
              <header key={focusedChapter.id} className="hb-rise mb-9">
                <span className="hb-numeral block text-[3.25rem] leading-none sm:text-[4rem]">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <p className="hb-eyebrow hb-eyebrow--accent mt-4">
                  {t('Chapter')} · {String(idx + 1).padStart(2, '0')} / {orderedIds.length}
                </p>
                <h1 className="hb-title mt-1.5 text-3xl sm:text-4xl">
                  {resolveLocalized(focusedChapter.title, LOCALE)}
                </h1>
              </header>
            )}

            <div
              key={`prose-${sessionId}-${focusedChapter?.id}-${chapterAttempt}`}
              className="hb-prose hb-fade"
            >
              {focusedChapter?.blocks.map((block) => (
                <BlockRenderer
                  key={block.id}
                  block={block}
                  locale={LOCALE}
                  onInteract={onWidgetInteract}
                />
              ))}
            </div>

            {focusedChapter && (
              <footer className="mt-12 border-t border-line pt-6">
                {deliveryError && (
                  <div role="alert" className="mb-4">
                    <p>{t('Training progress could not be saved. Retry this chapter.')}</p>
                    <button
                      type="button"
                      data-testid="training-chapter-retry"
                      className="hb-btn hb-btn--ghost"
                      onClick={() => setChapterAttempt((value) => value + 1)}
                    >
                      {t('Retry chapter')}
                    </button>
                  </div>
                )}
                {!chapterDone &&
                  !deliveryError &&
                  (dwellPending ? (
                    <DwellCountdown remainingSeconds={dwellRemaining} totalSeconds={dwellTotal} />
                  ) : (
                    <p className="mb-4 flex items-center gap-2.5 text-sm text-ink-faint">
                      <span aria-hidden className="hb-spinner !h-3.5 !w-3.5" />
                      {gateHint}
                    </p>
                  ))}
                {/* Back sits left, Continue right. `mr-auto` rather than justify-between so
                    Continue stays right-aligned on the first chapter, where there is no Back. */}
                <div className="flex items-center justify-end gap-3">
                  {idx > 0 && (
                    <button
                      type="button"
                      data-testid="training-chapter-back"
                      onClick={goBack}
                      className="hb-btn hb-btn--ghost hb-btn--pill mr-auto"
                    >
                      <span aria-hidden>←</span>
                      {t('Back')}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={!chapterDone}
                    onClick={advance}
                    className="hb-btn hb-btn--primary hb-btn--pill"
                  >
                    {isLast ? t('Continue to the test') : t('Continue')}
                    <span aria-hidden>→</span>
                  </button>
                </div>
              </footer>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
